import { installedSectorNodes } from '../adapters/installedSectors';
import i18n from '../i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, CalendarClock, Check, Download, Droplets, TimerReset } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import SoverdiSiteSelector from '../components/SoverdiSiteSelector';
import { useAgriSafeSoverdiDashboard } from '../hooks/useAgriSafeSoverdiDashboard';
import { useNodeHistory } from '../../hooks/useNodeHistory';
import { useFieldBindings } from '../../hooks/useFieldBindings';
import { iotApi } from '../../lib/iotApi';
import { buildSectorModel } from '../../lib/sectorOperationalModel';
import {
  ANALYSIS_METRICS,
  availableAnalysisMetrics,
  buildAnalysisHistory,
  integrateMeasuredWaterVolume,
  latestAnalysisMetric,
  type AnalysisMetricKey,
} from '../adapters/analysisHistory';
import {
  formatHistoryAxis,
  getDefaultDateWindow,
  resolveHistoryWindow,
  type DateWindow,
  type FeedbackRange,
} from '../adapters/sectorHistory';

const INVALID_PERIOD_TEST_ID = 'analysis-invalid-period';

type Range = FeedbackRange;
type ExportScope = 'sector' | 'parcel' | 'farm';


const METRIC_STROKES: Record<AnalysisMetricKey, string> = {
  pressure: '#0ea5e9',
  flow: '#22c55e',
  soilMoisture: '#2563eb',
  soilTemperature: '#f97316',
  soilEc: '#8b5cf6',
  airTemperature: '#ef4444',
  airHumidity: '#06b6d4',
  battery: '#64748b',
};

interface IrrigationRunStep {
  binding_id: string;
  order: number;
  duration_seconds: number;
  overlap_to_next_seconds?: number | null;
}

interface IrrigationRun {
  id: string;
  farm_id: string;
  program_id: string;
  scheduled_for: string;
  status: string;
  program_snapshot?: {
    name?: string;
    steps?: IrrigationRunStep[];
  };
}

interface PlannedOccurrence {
  id: string;
  scheduledFor: string;
  programName: string;
  status: string;
  plannedSeconds: number;
  parcels: string[];
}


const formatMetric = (key: AnalysisMetricKey, value: number | null): string => {
  if (value === null) return '—';
  const definition = ANALYSIS_METRICS.find((metric) => metric.key === key);
  if (!definition) return String(value);
  return `${value.toFixed(definition.digits)} ${definition.unit}`;
};

const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return hours > 0 ? i18n.t("sectorViews.durationHours", { hours, remaining }) : i18n.t("sectorViews.durationMinutes", { minutes });
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(i18n.language, {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const csvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadCsv = (name: string, rows: unknown[][]): void => {
  const content = `\ufeff${rows.map((row) => row.map(csvCell).join(';')).join('\n')}`;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const SoverdiAnalysisR7: React.FC = () => {
  const { t } = useTranslation('common');
  const STATUS_LABELS: Record<string, string> = {
    SCHEDULED: i18n.t("sectorViews.Planifiee"),
    BLOCKED: i18n.t("sectorViews.Bloquee"),
    WAITING_OPERATOR_CONFIRMATION: 'Attente confirmation',
    STARTING: i18n.t("sectorViews.Demarrage_2"),
    RUNNING: i18n.t("sectorViews.running"),
    TRANSITIONING: i18n.t("sectorViews.Transition"),
    STOPPING: i18n.t("sectorViews.Arret"),
    COMPLETED: i18n.t("sectorViews.Terminee"),
    STOPPED: i18n.t("sectorViews.Arretee"),
    FAILED: i18n.t("sectorViews.Echec"),
    UNCERTAIN: i18n.t("sectorViews.Etat_incertain"),
  };
  const RANGE_LABELS: Record<Range, string> = {
    '24h': i18n.t("sectorViews.24_h"),
    '48h': i18n.t("sectorViews.48_h"),
    '7d': i18n.t("sectorViews.7_j"),
    '30d': i18n.t("sectorViews.30_j"),
    custom: i18n.t("sectorViews.Date_a_date"),
  };
  const { topology, sectors: legacySectors, operationalSectors, technicalSectors, parcelDisplayName, nodes, placement, loading, loadError, placementUnavailable, bindingsUnavailable } = useAgriSafeSoverdiDashboard();
  const sectors = operationalSectors ?? legacySectors;
  const { parcelBindings: rawParcelBindings, farmId: bindingsFarmId, pressureSensorByBinding, loaded: bindingsLoaded, error: rawBindingsError } = useFieldBindings();
  const bindingFarmMatches = Boolean(topology?.farmId) && bindingsFarmId === topology?.farmId;
  const bindingsError = rawBindingsError || !bindingFarmMatches;
  const parcelBindings = useMemo(() => bindingsLoaded && !bindingsError ? rawParcelBindings : [], [rawParcelBindings, bindingsLoaded, bindingsError]);
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const [range, setRange] = useState<Range>('24h');
  const [dateWindow, setDateWindow] = useState<DateWindow>(getDefaultDateWindow);
  const [selectedMetrics, setSelectedMetrics] = useState<AnalysisMetricKey[]>(['pressure', 'flow', 'soilMoisture']);
  const [runs, setRuns] = useState<IrrigationRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState(false);
  const [exportScope, setExportScope] = useState<ExportScope>('sector');
  const [exportParcel, setExportParcel] = useState('');

  const siteNames = useMemo(() => topology?.sites.map((site) => site.name) ?? [], [topology]);
  const effectiveSiteName = selectedSiteName || siteNames[0] || '';
  const siteSectors = useMemo(
    () => sectors.filter((sector) => sector.siteName === effectiveSiteName),
    [sectors, effectiveSiteName],
  );
  const selectedSector = useMemo(
    () => siteSectors.find((sector) => sector.id === selectedSectorId) ?? siteSectors[0] ?? null,
    [siteSectors, selectedSectorId],
  );

  const model = useMemo(() => {
    if (!selectedSector) return null;
    const binding = parcelBindings.find(b => b.binding_id === selectedSector.bindingId);
    return buildSectorModel({
      nodes,
      placement,
      siteName: selectedSector.siteName,
      sectorCode: selectedSector.sectorCode,
      now: Date.now(),
      ...(selectedSector.bindingId ? {
        commissionedNodes: binding && bindingsLoaded && !bindingsError && bindingsFarmId === topology?.farmId
          ? installedSectorNodes({ binding, bindings: parcelBindings, nodes, pressureSensorByBinding: pressureSensorByBinding ?? {}, placement,
            sector: technicalSectors?.find(s => s.siteName === binding.site && s.sectorCode === binding.sector_code), includeSharedController: true }) : [],
      } : {}),
    });
  }, [selectedSector, nodes, placement, pressureSensorByBinding, bindingsLoaded, bindingsError, bindingsFarmId, topology?.farmId, parcelBindings, technicalSectors]);

  const bounds = useMemo(
    () => resolveHistoryWindow(range, range === 'custom' ? dateWindow : undefined),
    [range, dateWindow],
  );
  const soilRead = useNodeHistory(bounds.valid ? (model?.soilSensor?.id ?? null) : null, bounds.valid ? bounds.hours : 0);
  const pressureRead = useNodeHistory(bounds.valid ? (model?.pressureSensor?.id ?? null) : null, bounds.valid ? bounds.hours : 0);
  const flowRead = useNodeHistory(bounds.valid ? (model?.controller?.id ?? null) : null, bounds.valid ? bounds.hours : 0);

  const history = useMemo(
    () => buildAnalysisHistory([
      {
        series: soilRead.series,
        metricKeys: ['soilMoisture', 'soilTemperature', 'soilEc', 'airTemperature', 'airHumidity', 'battery'],
      },
      { series: pressureRead.series, metricKeys: ['pressure', 'battery'] },
      { series: flowRead.series, metricKeys: ['flow', 'battery'] },
    ], bounds),
    [soilRead.series, pressureRead.series, flowRead.series, bounds],
  );
  const availableMetrics = useMemo(() => availableAnalysisMetrics(history), [history]);
  const activeMetrics = selectedMetrics.filter((key) => availableMetrics.includes(key));
  const measuredWater = useMemo(
    () => integrateMeasuredWaterVolume(flowRead.series, bounds),
    [flowRead.series, bounds],
  );

  useEffect(() => {
    if (availableMetrics.length === 0) return;
    setSelectedMetrics((current) => {
      const valid = current.filter((key) => availableMetrics.includes(key));
      return valid.length > 0 ? valid : availableMetrics.slice(0, 3);
    });
  }, [availableMetrics.join('|')]);

  const farmId = topology?.farmId ?? '';
  useEffect(() => {
    if (!farmId) {
      setRuns([]);
      setRunsLoading(false);
      setRunsError(false);
      return;
    }
    let active = true;
    setRunsLoading(true);
    setRunsError(false);
    iotApi
      .getJson<IrrigationRun[]>(`/iot/irrigation-program-runs?farm_id=${encodeURIComponent(farmId)}&limit=500`)
      .then((rows) => {
        if (!active) return;
        setRuns(Array.isArray(rows) ? rows : []);
        setRunsLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setRuns([]);
        setRunsLoading(false);
        setRunsError(true);
      });
    return () => { active = false; };
  }, [farmId]);

  const bindingById = useMemo(
    () => new Map(parcelBindings.map((binding) => [binding.binding_id, binding])),
    [parcelBindings],
  );

  const occurrences = useMemo<PlannedOccurrence[]>(() => {
    if (!selectedSector) return [];
    return runs
      .filter((run) => {
        const scheduled = Date.parse(run.scheduled_for);
        return run.farm_id === farmId && Number.isFinite(scheduled) && scheduled >= bounds.startMs && scheduled <= bounds.endMs;
      })
      .map((run) => {
        const matched = (run.program_snapshot?.steps ?? []).flatMap((step) => {
          const binding = bindingById.get(step.binding_id);
          if (!binding) return [];
          if (selectedSector.bindingId && binding.binding_id !== selectedSector.bindingId) return [];
          if (binding.site !== selectedSector.siteName || binding.sector_code !== selectedSector.sectorCode) return [];
          return [{ step, binding }];
        });
        if (matched.length === 0) return null;
        return {
          id: run.id,
          scheduledFor: run.scheduled_for,
          programName: run.program_snapshot?.name || i18n.t("sectorViews.Programme_d_irrigation"),
          status: run.status,
          plannedSeconds: matched.reduce((sum, entry) => sum + entry.step.duration_seconds, 0),
          parcels: Array.from(new Set(matched.map((entry) => parcelDisplayName?.(entry.binding) ?? entry.binding.parcel))),
        };
      })
      .filter((row): row is PlannedOccurrence => row !== null)
      .sort((a, b) => Date.parse(b.scheduledFor) - Date.parse(a.scheduledFor));
  }, [runs, bounds.startMs, bounds.endMs, bindingById, selectedSector, parcelDisplayName, t, farmId]);

  const allPeriodRuns = useMemo(
    () => runs.filter((run) => {
      const scheduled = Date.parse(run.scheduled_for);
      return run.farm_id === farmId && Number.isFinite(scheduled) && scheduled >= bounds.startMs && scheduled <= bounds.endMs;
    }),
    [runs, bounds.startMs, bounds.endMs, farmId],
  );

  const plannedSeconds = occurrences.reduce((sum, occurrence) => sum + occurrence.plannedSeconds, 0);
  const historyLoading = soilRead.loading || pressureRead.loading || flowRead.loading;
  const historyError = soilRead.error || pressureRead.error || flowRead.error;
  const runListPossiblyCapped = runs.length >= 500;
  const exportBlocked = !bounds.valid
    || runsLoading
    || runsError
    || runListPossiblyCapped
    || (exportScope !== 'farm' && (!bindingsLoaded || bindingsError))
    || (exportScope === 'sector' && (
      loading
      || loadError
      || placementUnavailable
      || historyLoading
      || historyError
    ));

  const controllerBindings = useMemo(
    () => model?.controller?.id
      ? parcelBindings.filter((binding) => binding.controller_node_id === model.controller?.id)
      : [],
    [parcelBindings, model?.controller?.id],
  );
  const controllerUnits = new Set(controllerBindings.map((binding) => binding.binding_id));
  const controllerSites = new Set(controllerBindings.map((binding) => binding.site));
  const waterScope = controllerUnits.size === 1
    ? i18n.t("sectorViews.Secteur_selectionne")
    : controllerSites.size === 1
      ? i18n.t("sectorViews.Controleur_partage_value0", { value0: Array.from(controllerSites)[0] })
      : controllerBindings.length > 0
        ? i18n.t("sectorViews.Controleur_partage")
        : i18n.t("sectorViews.Debit_mesure");

  const handleSiteChange = (siteName: string) => {
    setSelectedSiteName(siteName);
    setSelectedSectorId('');
    setExportParcel('');
  };

  const toggleMetric = (key: AnalysisMetricKey) => {
    if (!availableMetrics.includes(key)) return;
    setSelectedMetrics((current) => (
      current.includes(key)
        ? (current.length === 1 ? current : current.filter((item) => item !== key))
        : [...current, key]
    ));
  };

  const exportCsv = () => {
    if (!topology || exportBlocked || (exportScope !== 'farm' && !selectedSector)) return;
    const period = range === 'custom'       ? `${dateWindow.start} → ${dateWindow.end}`
      : RANGE_LABELS[range];
    const selectedParcel = exportParcel || (selectedSector?.bindingId ? selectedSector.displayName : selectedSector?.canonicalParcelNames?.[0]) || '';
    const rows: unknown[][] = [
      [i18n.t("sectorViews.Agri_Safe_Rapport_d_analyse")],
      ['Exploitation', topology.clientName],
      [i18n.t("sectorViews.Periode"), period],
      [i18n.t("sectorViews.Portee"), exportScope === 'farm' ? i18n.t("sectorViews.wholeFarm") : exportScope === 'parcel' ? i18n.t("sectorViews.Parcelle") : i18n.t("sectorViews.Secteur")],
    ];

    if (exportScope === 'farm') {
      rows.push(
        [i18n.t("sectorViews.Sites"), topology.summary.siteCount],
        [i18n.t("sectorViews.Secteurs"), (bindingsUnavailable || bindingsError) && topology.sites.some(site => site.name === 'Roubia') ? i18n.t("availabilityUnavailable") : sectors.length],
        [i18n.t("sectorViews.Parcelles"), topology.summary.parcelCount],
        [i18n.t("sectorViews.Occurrences_planifiees_materialisees_visibles"), allPeriodRuns.length],
        ['Eau totale exploitation', '—'],
        [i18n.t("sectorViews.Note"), i18n.t("sectorViews.L_agregation_hydrique_exploitation_n_est_pas_attribuee_depuis_cette_vu")],
      );
    } else if (exportScope === 'parcel') {
      const parcelOccurrences = occurrences.filter((occurrence) => occurrence.parcels.includes(selectedParcel));
      rows.push(
        ['Site', selectedSector!.siteName],
        [i18n.t("sectorViews.Secteur"), selectedSector!.displayName],
        [i18n.t("sectorViews.Parcelle"), selectedParcel || '—'],
        [i18n.t("sectorViews.Occurrences_planifiees_materialisees"), parcelOccurrences.length],
        ['Eau parcelle', '—'],
        [i18n.t("sectorViews.Note"), i18n.t("sectorViews.La_telemetrie_hydraulique_n_est_pas_attribuee_a_une_parcelle_tant_que_")],
        [],
        [i18n.t("sectorViews.Debut_prevu"), i18n.t("sectorViews.Programme_3"), i18n.t("status"), i18n.t("sectorViews.Duree_prevue")],
        ...parcelOccurrences.map((occurrence) => [
          formatDateTime(occurrence.scheduledFor), occurrence.programName,
          STATUS_LABELS[occurrence.status] || occurrence.status, formatDuration(occurrence.plannedSeconds),
        ]),
      );
    } else {
      rows.push(
        ['Site', selectedSector!.siteName],
        [i18n.t("sectorViews.Secteur"), selectedSector!.displayName],
        [i18n.t("sectorViews.Eau_cumulee_a_partir_du_debit_mesure"), measuredWater.volumeM3 === null ? '—' : `${measuredWater.volumeM3.toFixed(2)} m³`],
        [i18n.t("sectorViews.Perimetre_eau"), waterScope],
        [i18n.t("sectorViews.Minutes_de_debit_reellement_integrees"), Math.round(measuredWater.integratedMinutes)],
        [i18n.t("sectorViews.Occurrences_planifiees_materialisees"), occurrences.length],
        [i18n.t("sectorViews.Duree_programmee_cumulee"), formatDuration(plannedSeconds)],
        [],
        [i18n.t("sectorViews.Mesure"), i18n.t("sectorViews.Derniere_valeur")],
        ...availableMetrics.map((key) => {
          const definition = ANALYSIS_METRICS.find((metric) => metric.key === key);
          return [definition?.label || key, formatMetric(key, latestAnalysisMetric(history, key))];
        }),
        [],
        [i18n.t("sectorViews.Debut_prevu"), i18n.t("sectorViews.Programme_3"), i18n.t("sectorViews.Parcelles"), i18n.t("status"), i18n.t("sectorViews.Duree_prevue")],
        ...occurrences.map((occurrence) => [
          formatDateTime(occurrence.scheduledFor), occurrence.programName, occurrence.parcels.join(' · '),
          STATUS_LABELS[occurrence.status] || occurrence.status, formatDuration(occurrence.plannedSeconds),
        ]),
      );
    }

    const scopeName = exportScope === 'farm' ? i18n.t("sectorViews.exploitation") : exportScope === 'parcel' ? i18n.t("sectorViews.parcelle") : i18n.t("sectorViews.secteur");
    downloadCsv(`agrisafe-analyse-${scopeName}.csv`, rows);
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" /></div>;
  }

  if (!topology) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-sm text-[var(--text-secondary)]" data-testid="analysis-unavailable">
        {i18n.t("sectorViews.Analyse_indisponible_Aucune_donnee_historique_fictive_n_est_affichee")}</div>
    );
  }

  const parcelChoices = selectedSector?.bindingId ? [selectedSector.displayName] : selectedSector?.canonicalParcelNames ?? [];

  return (
    <div className="space-y-5 pb-[calc(7.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6" data-testid="soverdi-analysis-r7">
      <SoverdiSiteSelector sites={siteNames} selected={effectiveSiteName} onSelect={handleSiteChange} />

      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-primary)]">
            <BarChart3 className="h-5 w-5" />
            <span className="text-sm font-medium">{i18n.t("sectorViews.Analyse")}</span>
          </div>
          <h1 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">{i18n.t("sectorViews.Historique_telemetrie")}</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            {i18n.t("sectorViews.Mesures_reelles_du_secteur_eau_derivee_du_debit_calibre_et_occurrences")}</p>
        </div>
        <span className="inline-flex w-fit items-center rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
          {i18n.t("sectorViews.Donnees_reelles")}</span>
      </section>

      {(loadError || !bounds.valid || historyError || runsError || bindingsError || bounds.truncated) && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status" data-testid={!bounds.valid ? INVALID_PERIOD_TEST_ID : undefined}>
          {!bounds.valid
            ? t('invalidDateWindow')
            : bounds.truncated
              ? i18n.t("sectorViews.La_telemetrie_est_disponible_sur_30_jours_maximum_la_periode_demandee_")
              : i18n.t("sectorViews.Certaines_donnees_d_analyse_sont_indisponibles_Les_informations_manqua")}
        </section>
      )}

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(220px,1fr)_auto] lg:items-end">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Secteur")}</span>
            <select
              value={selectedSector?.id ?? ''}
              onChange={(event) => { setSelectedSectorId(event.target.value); setExportParcel(''); }}
              data-testid="analysis-sector-select"
              className="mt-2 min-h-[44px] w-full rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 text-sm font-medium text-[var(--text-primary)]"
            >
              {siteSectors.map((sector) => (
                <option key={sector.id} value={sector.id}>{sector.displayName}</option>
              ))}
            </select>
          </label>

          <div>
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Periode")}</span>
            <div className="mt-2 inline-flex flex-wrap rounded-xl bg-[var(--gray-100)] p-1">
              {(Object.keys(RANGE_LABELS) as Range[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setRange(item)}
                  data-testid={`analysis-range-${item}`}
                  className={`min-h-[36px] rounded-lg px-3 text-xs font-semibold ${range === item ? 'bg-[var(--surface)] text-[var(--color-primary)] shadow-sm' : 'text-[var(--text-secondary)]'}`}
                >
                  {RANGE_LABELS[item]}
                </button>
              ))}
            </div>
          </div>
        </div>
        {range === 'custom' && (
          <div className="mt-4 flex flex-wrap gap-3 border-t border-[var(--border-light)] pt-4">
            <label className="text-xs text-[var(--text-secondary)]">{i18n.t("sectorViews.Du")}<input type="date" value={dateWindow.start} max={dateWindow.end || undefined} aria-invalid={!bounds.valid} onChange={(event) => setDateWindow((current) => ({ ...current, start: event.target.value }))} className="ml-2 min-h-[38px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-2 text-[var(--text-primary)]" /></label>
            <label className="text-xs text-[var(--text-secondary)]">{i18n.t("sectorViews.Au")}<input type="date" value={dateWindow.end} min={dateWindow.start || undefined} aria-invalid={!bounds.valid} onChange={(event) => setDateWindow((current) => ({ ...current, end: event.target.value }))} className="ml-2 min-h-[38px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-2 text-[var(--text-primary)]" /></label>
          </div>
        )}
      </section>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]"><CalendarClock className="h-4 w-4" />{i18n.t("sectorViews.Occurrences")}</div>
          <div className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="analysis-occurrence-count">{!bounds.valid || runsLoading || !bindingsLoaded || bindingsError || runListPossiblyCapped ? '—' : occurrences.length}</div>
          <div className="mt-1 text-[11px] text-[var(--text-tertiary)]">{i18n.t("sectorViews.Planifiees_materialisees")}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]"><Droplets className="h-4 w-4" />{i18n.t("sectorViews.Eau_cumulee")}</div>
          <div className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="analysis-water-volume">
            {!bounds.valid || historyLoading || measuredWater.volumeM3 === null ? '—' : `${measuredWater.volumeM3.toFixed(2)} m³`}
          </div>
          <div className="mt-1 text-[11px] text-[var(--text-tertiary)]">{waterScope} {i18n.t("sectorViews.debit_mesure")}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]"><TimerReset className="h-4 w-4" />{i18n.t("sectorViews.Duree_programmee")}</div>
          <div className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="analysis-planned-duration">{!bounds.valid || runsLoading || !bindingsLoaded || bindingsError || runListPossiblyCapped ? '—' : formatDuration(plannedSeconds)}</div>
          <div className="mt-1 text-[11px] text-[var(--text-tertiary)]">{i18n.t("sectorViews.Pas_une_duree_terrain_confirmee")}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">{i18n.t("sectorViews.Mesures_disponibles")}</div>
          <div className="mt-2 text-2xl font-semibold text-[var(--text-primary)]" data-testid="analysis-metric-count">{!bounds.valid || historyLoading ? '—' : availableMetrics.length}</div>
          <div className="mt-1 text-[11px] text-[var(--text-tertiary)]">{i18n.t("sectorViews.Sur_la_periode_selectionnee")}</div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Historique_des_mesures")}</h2>
              <p className="mt-1 text-xs text-[var(--text-secondary)]">{i18n.t("sectorViews.Une_seule_courbe_activez_les_mesures_a_comparer_Les_unites_conservent_")}</p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="analysis-metric-toggles">
              {ANALYSIS_METRICS.map((metric) => {
                const available = availableMetrics.includes(metric.key);
                const selected = activeMetrics.includes(metric.key);
                return (
                  <button
                    key={metric.key}
                    type="button"
                    disabled={!available}
                    onClick={() => toggleMetric(metric.key)}
                    data-testid={`analysis-metric-${metric.key}`}
                    className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors ${selected ? 'border-[var(--color-primary)] text-[var(--color-primary)]' : 'border-[var(--border-light)] text-[var(--text-secondary)]'} disabled:cursor-not-allowed disabled:opacity-30`}
                  >
                    {selected && <Check className="h-3 w-3" />}{metric.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {historyLoading ? (
          <div className="flex h-[360px] items-center justify-center text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Chargement_de_l_historique")}</div>
        ) : history.length === 0 || activeMetrics.length === 0 ? (
          <div className="flex h-[360px] items-center justify-center px-6 text-center text-sm text-[var(--text-secondary)]" data-testid="analysis-chart-unavailable">{i18n.t("sectorViews.Aucune_mesure_exploitable_sur_cette_periode")}</div>
        ) : (
          <div className="h-[360px] p-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" tickFormatter={(value) => formatHistoryAxis(value, range)} minTickGap={28} />
                {activeMetrics.map((key, index) => {
                  const definition = ANALYSIS_METRICS.find((metric) => metric.key === key)!;
                  return <YAxis key={key} yAxisId={key} hide={index !== 0} unit={index === 0 ? ` ${definition.unit}` : undefined} width={62} />;
                })}
                <Tooltip labelFormatter={(value) => formatHistoryAxis(String(value), range)} />
                <Legend />
                {activeMetrics.map((key) => {
                  const definition = ANALYSIS_METRICS.find((metric) => metric.key === key)!;
                  return (
                    <Line
                      key={key}
                      yAxisId={key}
                      type="monotone"
                      dataKey={key}
                      name={`${definition.label} (${definition.unit})`}
                      connectNulls={false}
                      dot={false}
                      stroke={METRIC_STROKES[key]}
                      strokeWidth={2}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="flex flex-col gap-3 border-b border-[var(--border-light)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Historique_des_irrigations")}</h2>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">{i18n.t("sectorViews.Occurrences_durables_du_moteur_de_programmation_sur_la_periode_selecti")}</p>
          </div>
          {runListPossiblyCapped && <span className="text-xs font-medium text-amber-700">{i18n.t("sectorViews.Limite_API_de_500_runs_atteinte_liste_potentiellement_partielle")}</span>}
        </div>

        {!bounds.valid ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Corrigez_la_periode_avant_de_consulter_les_occurrences")}</div>
        ) : bindingsError ? (
          <div role="status" className="px-4 py-10 text-center text-sm" data-testid="analysis-bindings-unavailable">{t('availabilityUnavailable')}</div>
        ) : !bindingsLoaded || runsLoading ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Chargement_des_occurrences")}</div>
        ) : occurrences.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Aucune_occurrence_planifiee_ou_materialisee_pour_ce_secteur_sur_cette_")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="analysis-irrigation-table">
              <thead className="bg-[var(--gray-50)] text-left text-xs text-[var(--text-secondary)]">
                <tr>
                  <th className="px-4 py-3 font-medium">{i18n.t("sectorViews.Secteur_parcelles")}</th>
                  <th className="px-4 py-3 font-medium">{i18n.t("sectorViews.Origine")}</th>
                  <th className="px-4 py-3 font-medium">{i18n.t("sectorViews.Debut_prevu")}</th>
                  <th className="px-4 py-3 font-medium">{i18n.t("sectorViews.Duree_prevue")}</th>
                  <th className="px-4 py-3 font-medium">{i18n.t("sectorViews.Eau")}</th>
                  <th className="px-4 py-3 font-medium">{i18n.t("status")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-light)]">
                {occurrences.map((occurrence) => (
                  <tr key={occurrence.id}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--text-primary)]">{selectedSector?.displayName}</div>
                      <div className="mt-0.5 text-xs text-[var(--text-tertiary)]">{occurrence.parcels.join(' · ') || i18n.t("sectorViews.Parcelle_non_resolue")}</div>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{occurrence.programName}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{formatDateTime(occurrence.scheduledFor)}</td>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{formatDuration(occurrence.plannedSeconds)}</td>
                    <td className="px-4 py-3 text-[var(--text-tertiary)]">—</td>
                    <td className="px-4 py-3"><span className="rounded-full bg-[var(--gray-100)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)]">{STATUS_LABELS[occurrence.status] || occurrence.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-[var(--border-light)] bg-[var(--gray-50)] px-4 py-3 text-xs text-[var(--text-secondary)] sm:px-5">
          {i18n.t("sectorViews.Les_runs_actuellement_materialises_ne_prouvent_pas_encore_une_executio")}</div>
      </section>

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Rapports_exports")}</h2>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">{i18n.t("sectorViews.Export_metier_CSV_sans_identifiants_LoRaWAN_canaux_payloads_ni_donnees")}</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-medium text-[var(--text-secondary)]">
              {i18n.t("sectorViews.Perimetre")}<select value={exportScope} onChange={(event) => setExportScope(event.target.value as ExportScope)} className="mt-1 block min-h-[40px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-3 text-sm text-[var(--text-primary)]" data-testid="analysis-export-scope">
                <option value="sector">{i18n.t("sectorViews.Secteur_selectionne")}</option>
                <option value="parcel">{i18n.t("sectorViews.Parcelle")}</option>
                <option value="farm">{i18n.t("sectorViews.Toute_l_exploitation")}</option>
              </select>
            </label>
            {exportScope === 'parcel' && (
              <label className="text-xs font-medium text-[var(--text-secondary)]">
                {i18n.t("sectorViews.Parcelle")}<select value={exportParcel || parcelChoices[0] || ''} onChange={(event) => setExportParcel(event.target.value)} className="mt-1 block min-h-[40px] max-w-[280px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-3 text-sm text-[var(--text-primary)]">
                  {parcelChoices.map((parcel) => <option key={parcel} value={parcel}>{parcel}</option>)}
                </select>
              </label>
            )}
            <button type="button" onClick={exportCsv} disabled={exportBlocked} className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40" data-testid="analysis-export-csv">
              <Download className="h-4 w-4" /> {i18n.t("sectorViews.Exporter_CSV")}</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoverdiAnalysisR7;
